class ProductImageZoom {
  constructor() {
    this.currentImageIndex = 0;
    this.images = [];
    this.zoomLevel = 1;
    this.minZoom = 1;
    this.maxZoom = 3;
    this.zoomStep = 0.5;
    this.isDragging = false;
    this.startX = 0;
    this.startY = 0;
    this.scrollLeft = 0;
    this.scrollTop = 0;

    this.disableShopifyLightbox();
    this.createModal();
    this.attachEventListeners();
  }

  disableShopifyLightbox() {
    // Disable the product-modal element itself
    const productModals = document.querySelectorAll('product-modal');
    productModals.forEach((modal) => {
      modal.style.display = 'none';
      modal.style.pointerEvents = 'none';
      // Override the show method to do nothing
      if (modal.show) {
        modal.show = function () {};
      }
    });

    // Attach our custom click handlers to modal-opener elements
    document.querySelectorAll('modal-opener.product__modal-opener--image').forEach((opener) => {
      opener.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const mediaContainer = opener.closest('.product__media-item');
          if (mediaContainer) {
            const img = mediaContainer.querySelector('img');
            if (img) {
              this.collectAllImages();
              const index = this.findImageIndex(img.src);
              this.openModal(index >= 0 ? index : 0);
            }
          }
        },
        true
      );
    });
  }

  createModal() {
    const modalHTML = `
      <div class="product-zoom-modal" id="productZoomModal">
        <button class="product-zoom-modal__close" id="zoomModalClose" aria-label="Close">
          ×
        </button>
        
        <button class="product-zoom-modal__nav product-zoom-modal__nav--prev" id="zoomModalPrev" aria-label="Previous image">
          ‹
        </button>
        
        <div class="product-zoom-modal__container">
          <div class="product-zoom-modal__image-wrapper" id="zoomImageWrapper">
            <img class="product-zoom-modal__image" id="zoomImage" src="" alt="">
          </div>
        </div>
        
        <button class="product-zoom-modal__nav product-zoom-modal__nav--next" id="zoomModalNext" aria-label="Next image">
          ›
        </button>
        
        <div class="product-zoom-modal__controls">
          <button class="product-zoom-modal__button" id="zoomOut" aria-label="Zoom out">
            −
          </button>
          <div class="product-zoom-modal__zoom-level" id="zoomLevel">100%</div>
          <button class="product-zoom-modal__button" id="zoomIn" aria-label="Zoom in">
            +
          </button>
          <button class="product-zoom-modal__button" id="zoomReset" aria-label="Reset zoom" title="Reset">
            ⟲
          </button>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);

    this.modal = document.getElementById('productZoomModal');
    this.image = document.getElementById('zoomImage');
    this.imageWrapper = document.getElementById('zoomImageWrapper');
    this.closeBtn = document.getElementById('zoomModalClose');
    this.prevBtn = document.getElementById('zoomModalPrev');
    this.nextBtn = document.getElementById('zoomModalNext');
    this.zoomInBtn = document.getElementById('zoomIn');
    this.zoomOutBtn = document.getElementById('zoomOut');
    this.zoomResetBtn = document.getElementById('zoomReset');
    this.zoomLevelDisplay = document.getElementById('zoomLevel');
  }

  attachEventListeners() {
    // Attach click handlers to product images
    // Use capture phase to intercept before Shopify's modal-opener
    document.addEventListener(
      'click',
      (e) => {
        const productImage = e.target.closest('.product__media-toggle');
        if (productImage) {
          e.preventDefault();
          e.stopPropagation(); // Prevent Shopify's default lightbox from opening
          const mediaContainer = productImage.closest('.product__media-item');
          if (mediaContainer) {
            const img = mediaContainer.querySelector('img');
            if (img) {
              this.collectAllImages();
              const index = this.findImageIndex(img.src);
              this.openModal(index >= 0 ? index : 0);
            }
          }
        }
      },
      true
    );

    // Modal controls
    this.closeBtn.addEventListener('click', () => this.closeModal());
    this.prevBtn.addEventListener('click', () => this.showPrevImage());
    this.nextBtn.addEventListener('click', () => this.showNextImage());
    this.zoomInBtn.addEventListener('click', () => this.zoomIn());
    this.zoomOutBtn.addEventListener('click', () => this.zoomOut());
    this.zoomResetBtn.addEventListener('click', () => this.resetZoom());

    // Click image to toggle zoom
    this.image.addEventListener('click', (e) => {
      if (this.zoomLevel === 1) {
        this.zoomIn();
      } else {
        this.resetZoom();
      }
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.modal.classList.contains('active')) return;

      switch (e.key) {
        case 'Escape':
          this.closeModal();
          break;
        case 'ArrowLeft':
          this.showPrevImage();
          break;
        case 'ArrowRight':
          this.showNextImage();
          break;
        case '+':
        case '=':
          this.zoomIn();
          break;
        case '-':
          this.zoomOut();
          break;
        case '0':
          this.resetZoom();
          break;
      }
    });

    // Click outside to close
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.closeModal();
      }
    });

    // Drag to pan when zoomed
    this.imageWrapper.addEventListener('mousedown', (e) => this.startDrag(e));
    this.imageWrapper.addEventListener('mousemove', (e) => this.drag(e));
    this.imageWrapper.addEventListener('mouseup', () => this.endDrag());
    this.imageWrapper.addEventListener('mouseleave', () => this.endDrag());

    // Touch events for mobile
    this.imageWrapper.addEventListener('touchstart', (e) => this.startDrag(e));
    this.imageWrapper.addEventListener('touchmove', (e) => this.drag(e));
    this.imageWrapper.addEventListener('touchend', () => this.endDrag());

    // Mouse wheel to zoom
    this.imageWrapper.addEventListener(
      'wheel',
      (e) => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          if (e.deltaY < 0) {
            this.zoomIn();
          } else {
            this.zoomOut();
          }
        }
      },
      { passive: false }
    );
  }

  collectAllImages() {
    const productImages = document.querySelectorAll('.product__media-item img');
    this.images = Array.from(productImages).map((img) => ({
      src: img.src.replace(/_(small|compact|medium|large|grande|1024x1024|2048x2048|master)\./g, '_2048x2048.'),
      alt: img.alt,
    }));
  }

  findImageIndex(src) {
    return this.images.findIndex((img) => {
      const srcBase = src
        .split('?')[0]
        .replace(/_(small|compact|medium|large|grande|1024x1024|2048x2048|master)\./, '.');
      const imgBase = img.src
        .split('?')[0]
        .replace(/_(small|compact|medium|large|grande|1024x1024|2048x2048|master)\./, '.');
      return srcBase === imgBase || img.src.includes(srcBase) || srcBase.includes(img.src);
    });
  }

  openModal(index = 0) {
    this.currentImageIndex = index;
    this.showImage();
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
    this.resetZoom();
  }

  showImage() {
    if (this.images.length === 0) return;

    const currentImage = this.images[this.currentImageIndex];
    this.image.src = currentImage.src;
    this.image.alt = currentImage.alt;

    this.updateNavigationButtons();
  }

  showPrevImage() {
    if (this.currentImageIndex > 0) {
      this.currentImageIndex--;
      this.resetZoom();
      this.showImage();
    }
  }

  showNextImage() {
    if (this.currentImageIndex < this.images.length - 1) {
      this.currentImageIndex++;
      this.resetZoom();
      this.showImage();
    }
  }

  updateNavigationButtons() {
    this.prevBtn.disabled = this.currentImageIndex === 0;
    this.nextBtn.disabled = this.currentImageIndex === this.images.length - 1;

    if (this.images.length <= 1) {
      this.prevBtn.style.display = 'none';
      this.nextBtn.style.display = 'none';
    } else {
      this.prevBtn.style.display = 'flex';
      this.nextBtn.style.display = 'flex';
    }
  }

  zoomIn() {
    if (this.zoomLevel < this.maxZoom) {
      this.zoomLevel = Math.min(this.zoomLevel + this.zoomStep, this.maxZoom);
      this.applyZoom();
    }
  }

  zoomOut() {
    if (this.zoomLevel > this.minZoom) {
      this.zoomLevel = Math.max(this.zoomLevel - this.zoomStep, this.minZoom);
      this.applyZoom();
    }
  }

  resetZoom() {
    this.zoomLevel = this.minZoom;
    this.applyZoom();
    this.imageWrapper.scrollLeft = 0;
    this.imageWrapper.scrollTop = 0;
  }

  applyZoom() {
    const scale = this.zoomLevel;

    if (scale > 1) {
      this.image.style.transform = `scale(${scale})`;
      this.image.classList.add('zoomed');
      this.imageWrapper.classList.add('zoomed');
    } else {
      this.image.style.transform = 'scale(1)';
      this.image.classList.remove('zoomed');
      this.imageWrapper.classList.remove('zoomed');
    }

    this.zoomLevelDisplay.textContent = `${Math.round(scale * 100)}%`;
    this.zoomInBtn.disabled = scale >= this.maxZoom;
    this.zoomOutBtn.disabled = scale <= this.minZoom;
  }

  startDrag(e) {
    if (this.zoomLevel <= 1) return;

    this.isDragging = true;
    this.imageWrapper.style.cursor = 'grabbing';

    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;

    this.startX = clientX - this.imageWrapper.offsetLeft;
    this.startY = clientY - this.imageWrapper.offsetTop;
    this.scrollLeft = this.imageWrapper.scrollLeft;
    this.scrollTop = this.imageWrapper.scrollTop;
  }

  drag(e) {
    if (!this.isDragging) return;

    e.preventDefault();

    const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
    const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;

    const x = clientX - this.imageWrapper.offsetLeft;
    const y = clientY - this.imageWrapper.offsetTop;
    const walkX = (x - this.startX) * 2;
    const walkY = (y - this.startY) * 2;

    this.imageWrapper.scrollLeft = this.scrollLeft - walkX;
    this.imageWrapper.scrollTop = this.scrollTop - walkY;
  }

  endDrag() {
    this.isDragging = false;
    this.imageWrapper.style.cursor = this.zoomLevel > 1 ? 'grab' : 'default';
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Small delay to ensure modal-opener elements are initialized
    setTimeout(() => {
      new ProductImageZoom();
    }, 100);
  });
} else {
  // Small delay to ensure modal-opener elements are initialized
  setTimeout(() => {
    new ProductImageZoom();
  }, 100);
}
